import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function initials(name?: string, email?: string) {
  const source = (name || email || 'U').trim();
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

const UserMenu: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleLogout = () => {
    setOpen(false);
    logout();
    navigate('/login');
  };

  return (
    <div className="relative flex-shrink-0" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        className="w-10 h-10 rounded-full overflow-hidden border border-nova-border2 bg-nova-sky text-white font-semibold text-sm flex items-center justify-center hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-nova-sky/40"
      >
        {user?.profilePicture ? (
          <img
            src={user.profilePicture}
            alt=""
            referrerPolicy="no-referrer"
            className="w-full h-full object-cover"
          />
        ) : (
          initials(user?.name, user?.email)
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-48 rounded-xl border border-nova-border bg-nova-bg3 shadow-nova-lg py-1 z-[60]"
        >
          <Link
            role="menuitem"
            to="/profile"
            onClick={() => setOpen(false)}
            className="block px-4 py-2.5 text-sm text-nova-text2 hover:bg-nova-bg2 hover:text-nova-text"
          >
            Profile
          </Link>
          <Link
            role="menuitem"
            to="/billing"
            onClick={() => setOpen(false)}
            className="block px-4 py-2.5 text-sm text-nova-text2 hover:bg-nova-bg2 hover:text-nova-text"
          >
            Billing
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={handleLogout}
            className="w-full text-left px-4 py-2.5 text-sm text-nova-text2 hover:bg-nova-bg2 hover:text-nova-text"
          >
            Logout
          </button>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
